import { object, string, number, date } from "yup";
import { ibanValidation } from "@shared-kernel/primary/forms/validations/iban";
import { REQUIRED_FIELD } from "@shared-kernel/primary/forms/constants";
import { MAX_BATCH_PRICE, MIN_BATCH_PRICE } from "@academy-context/shared/domain/constants/batch-price";
import { getMinFiscalPeriodDate } from "@shared-kernel/primary/shared/date/min-fiscal-period-date";
import type {
  SubmitInvoiceBody,
  ExternalProviderDashboardVM,
} from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import { normalizeIban } from "@utils/formatting";

export interface InvoiceFormInputs {
  iban: string;
  invoiceNumber: string;
  invoiceUrl: string;
  price: number;
  invoiceDate: Date;
  invoiceDeadline: Date;
}

// invoiceDate and invoiceDeadline are not included in defaultValues because they are set relative to now when the form is initialized
export const defaultInvoiceValues: Omit<InvoiceFormInputs, "invoiceDate" | "invoiceDeadline"> = {
  iban: "",
  invoiceNumber: "",
  invoiceUrl: "",
  price: 0,
};

export const createInvoiceFormSchema = (now: Date) =>
  object().shape({
    iban: ibanValidation(),
    invoiceNumber: string().required(REQUIRED_FIELD).max(50, "Maximum 50 caracteres"),
    invoiceUrl: string().required("Veuillez telecharger une facture"),
    price: number()
      .required(REQUIRED_FIELD)
      .min(MIN_BATCH_PRICE, `Le montant doit être d'au moins ${MIN_BATCH_PRICE} €`)
      .max(MAX_BATCH_PRICE, `Le montant ne peut pas dépasser ${MAX_BATCH_PRICE} €`),
    invoiceDate: date()
      .required(REQUIRED_FIELD)
      .min(getMinFiscalPeriodDate(now), "La date ne peut pas être antérieure à la période autorisée")
      .max(now, "La date de facturation ne peut pas être dans le futur"),
    invoiceDeadline: date()
      .required(REQUIRED_FIELD)
      .test("after-invoice-date", "La date d'échéance doit être postérieure à la date de facturation", function (value) {
        const { invoiceDate } = this.parent;
        if (!invoiceDate || !value) return true;
        return value.getTime() > invoiceDate.getTime();
      }),
  });

export const formatInvoiceDataToFormData = (
  data: ExternalProviderDashboardVM,
): Omit<InvoiceFormInputs, "invoiceDate" | "invoiceDeadline"> => {
  return {
    iban: data.provider.iban ?? defaultInvoiceValues.iban,
    invoiceNumber: data.invoice?.invoiceNumber ?? defaultInvoiceValues.invoiceNumber,
    invoiceUrl: data.invoice?.invoiceUrl ?? defaultInvoiceValues.invoiceUrl,
    price: data.invoice?.amount ?? data.training.price,
  };
};

export const formatFormDataToBody = (formData: InvoiceFormInputs): SubmitInvoiceBody => {
  return {
    providerIban: normalizeIban(formData.iban),
    invoiceNumber: formData.invoiceNumber,
    invoiceUrl: formData.invoiceUrl,
    price: formData.price,
    invoiceDate: formData.invoiceDate.toISOString(),
    invoiceDeadline: formData.invoiceDeadline.toISOString(),
  };
};
